import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatDate(date) {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(date));
}

export function formatDateShort(date) {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(new Date(date));
}

export function formatTime(seconds) {
  if (!seconds || seconds === 0) return '-';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}min`);
  if (s > 0 && h === 0) parts.push(`${s}s`);
  return parts.join(' ') || '-';
}

/**
 * Format duration string to a more readable format
 * Converts backend duration (e.g., "5d 3h 20m") to "5 Dias 3h 20min"
 * For periods > 30 days, shows months
 */
export function formatDuration(durationStr) {
  if (!durationStr || durationStr === '-') return '-';
  
  // Parse the duration string from backend (format: "Xd Xh Xm" or "Xh Xm" or "Xm Xs")
  const parts = durationStr.toLowerCase().split(' ');
  let totalMinutes = 0;
  
  for (const part of parts) {
    if (part.includes('d')) {
      totalMinutes += parseInt(part) * 24 * 60;
    } else if (part.includes('h')) {
      totalMinutes += parseInt(part) * 60;
    } else if (part.includes('m') && !part.includes('s')) {
      totalMinutes += parseInt(part);
    }
  }
  
  // If less than 24 hours, return original format
  if (totalMinutes < 24 * 60) {
    return durationStr;
  }
  
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  
  // Format based on duration - months for > 30 days
  if (days >= 30) {
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    if (remainingDays > 0) {
      return `${months} ${months === 1 ? 'mês' : 'meses'} ${remainingDays}d`;
    }
    return `${months} ${months === 1 ? 'mês' : 'meses'}`;
  } else {
    // Days format with hours
    if (hours > 0) {
      return `${days} ${days === 1 ? 'Dia' : 'Dias'} ${hours}h ${minutes > 0 ? minutes + 'min' : ''}`.trim();
    }
    return `${days} ${days === 1 ? 'Dia' : 'Dias'}`;
  }
}

export const SEVERITY_LABELS = {
  0: 'Not classified',
  1: 'Information',
  2: 'Warning',
  3: 'Average',
  4: 'High',
  5: 'Disaster'
};

export const SEVERITY_COLORS = {
  0: '#97AAB3',
  1: '#7499FF',
  2: '#FFC859',
  3: '#FFA059',
  4: '#E97659',
  5: '#E45959'
};

export function getSeverityClass(severity) {
  const classes = {
    0: 'bg-gray-500',
    1: 'bg-blue-500',
    2: 'bg-yellow-500',
    3: 'bg-orange-500',
    4: 'bg-red-500',
    5: 'bg-red-700'
  };
  return classes[severity] || classes[0];
}


/**
 * Export data to CSV file
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Array of { key: 'field', label: 'Header' }
 * @param {string} filename - Name of the file (without extension)
 */
export function exportToCSV(data, columns, filename) {
  if (!data || data.length === 0) {
    return;
  }

  // Create CSV header
  const header = columns.map(col => `"${col.label}"`).join(';');
  
  // Create CSV rows
  const rows = data.map(row => {
    return columns.map(col => {
      let value = row[col.key];
      
      // Handle nested values with dot notation
      if (col.key.includes('.')) {
        const keys = col.key.split('.');
        value = keys.reduce((obj, key) => obj?.[key], row);
      }
      
      // Handle arrays
      if (Array.isArray(value)) {
        value = value.join(', ');
      }
      
      // Handle null/undefined
      if (value === null || value === undefined) {
        value = '';
      }
      
      // Escape quotes and wrap in quotes
      return `"${String(value).replace(/"/g, '""')}"`;
    }).join(';');
  });

  // Combine header and rows
  const csv = [header, ...rows].join('\n');
  
  // Add BOM for UTF-8 encoding (helps Excel recognize special characters)
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
  
  // Create download link
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

